import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import LightIcon from '@material-ui/icons/WbSunny';
import {
  boschBlue,
  boschGray,
  boschGreen,
  boschRed,
  boschWhite,
  boschYellow,
} from './boschColors';
import { boschPageLayoutExtension } from './pageLayout';

/**
 * Bosch-branded font stack. Bosch Sans is a corporate font, so we fall back
 * to common sans-serif faces if it isn't installed locally.
 */
const boschFontFamily =
  '"Bosch Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';

/**
 * Each entity type gets its own gradient — same palette as the QuickStatsCards
 * on the home page so the design feels consistent across the app.
 *
 * The keys here MUST match the `spec.type` values used by the entities the
 * theme will render. Backstage's `getPageTheme({ themeId })` falls back to
 * the `home` entry whenever a key isn't found, which makes the Scaffolder
 * template cards (`<TemplateCard>` from `plugin-scaffolder-react/next`) all
 * look identical — every uncategorised type renders with the same blue.
 *
 * Adding a new template type means adding an entry below; otherwise its
 * card on `/create` will look identical to every other unmapped type.
 *
 * Palette + shape choices follow the Bosch brand guide:
 *
 *   - Blue = primary brand / production stuff (`service`, `website`, `app`).
 *   - Green = "go / live / informational" (`apis`, `tool`, `content-page`).
 *   - Gray = neutral / utility (`library`, `infrastructure`, `other`).
 *   - `wave2` and `round` shapes are reserved for secondary categories so
 *     primary services don't lose their distinctive `wave` silhouette.
 */
const boschPageTheme = {
  home: genPageTheme({
    colors: [boschBlue[40], boschBlue[55]],
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: [boschBlue[20], boschBlue[40]],
    shape: shapes.wave2,
  }),
  tool: genPageTheme({
    colors: [boschGreen[30], boschGreen[40]],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: [boschBlue[40], boschBlue[55]],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: [boschBlue[55], boschBlue[65]],
    shape: shapes.wave,
  }),
  library: genPageTheme({
    colors: [boschGray[35], boschGray[20]],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: [boschGray[25], boschGray[15]],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: [boschBlue[10], boschBlue[25]],
    shape: shapes.wave,
  }),
  apis: genPageTheme({
    colors: [boschGreen[40], boschGreen[50]],
    shape: shapes.wave,
  }),
  // ─── Custom Bosch DF template categories ────────────────────────
  // Both keys below were previously missing and falling back to the
  // `home` gradient (Bosch Blue 40-55), which is why every template
  // card on `/create` looked the same shade of blue. Mapping them
  // explicitly is the whole fix.

  // Infrastructure-as-Code templates (Terraform, Helm, Bicep, etc.).
  // Dark gray gradient reads as "industrial / platform-level"
  // without competing with the blue brand colour reserved for
  // service-facing UIs.
  infrastructure: genPageTheme({
    colors: [boschGray[30], boschGray[15]],
    shape: shapes.wave,
  }),
  // Admin-authored portal content pages (this repo's
  // `content-pages` module). Bosch Green with `wave2` keeps it
  // visually distinct from BOTH `tool`/`apis` (which already use
  // green) AND `documentation` (which uses `wave2` but with blue),
  // so a content-page card never collides with another category.
  'content-page': genPageTheme({
    colors: [boschGreen[40], boschGreen[50]],
    shape: shapes.wave2,
  }),
};

const boschLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: boschBlue[50],
        light: boschBlue[60],
        dark: boschBlue[40],
        contrastText: boschWhite,
      },
      secondary: {
        main: boschBlue[30],
        light: boschBlue[40],
        dark: boschBlue[20],
        contrastText: boschWhite,
      },
      error: {
        main: boschRed[50],
        light: boschRed[85],
        dark: boschRed[40],
        contrastText: boschWhite,
      },
      warning: {
        main: boschYellow[85],
        light: boschYellow[95],
        dark: boschYellow[80],
        contrastText: boschGray[15],
      },
      success: {
        main: boschGreen[50],
        light: boschGreen[90],
        dark: boschGreen[40],
        contrastText: boschWhite,
      },
      info: {
        main: boschBlue[50],
        light: boschBlue[85],
        dark: boschBlue[40],
        contrastText: boschWhite,
      },
      background: {
        default: boschGray[95],
        paper: boschWhite,
      },
      text: {
        primary: boschGray[15],
        secondary: boschGray[50],
      },
      textContrast: boschGray[15],
      textVerySubtle: boschGray[70],
      textSubtle: boschGray[50],
      highlight: boschYellow[95],
      errorBackground: boschRed[95],
      warningBackground: boschYellow[95],
      infoBackground: boschBlue[95],
      errorText: boschRed[50],
      infoText: boschBlue[40],
      warningText: boschYellow[50],
      linkHover: boschBlue[40],
      link: boschBlue[50],
      gold: boschYellow[85],
      bursts: {
        ...palettes.light.bursts,
        fontColor: boschWhite,
        slackChannelText: boschBlue[95],
        backgroundColor: {
          default: boschBlue[40],
        },
        gradient: {
          linear: `linear-gradient(135deg, ${boschBlue[30]} 0%, ${boschBlue[50]} 100%)`,
        },
      },
      banner: {
        info: boschBlue[50],
        error: boschRed[50],
        text: boschWhite,
        link: boschWhite,
        closeButtonColor: boschWhite,
        warning: boschYellow[85],
      },
      border: boschGray[85],
      tabbar: {
        indicator: boschBlue[50],
      },
      navigation: {
        ...palettes.light.navigation,
        // Dark sidebar — original boschGray[20], lifted one step to [25].
        background: boschGray[25],
        indicator: boschBlue[50],
        color: boschGray[80],
        selectedColor: boschWhite,
        navItem: {
          hoverBackground: 'rgba(255, 255, 255, 0.06)',
        },
        submenu: {
          background: boschGray[20],
        },
      },
      pinSidebarButton: {
        icon: boschGray[80],
        background: boschGray[20],
      },
    },
    defaultPageTheme: 'home',
    fontFamily: boschFontFamily,
  }),
  pageTheme: boschPageTheme,
  components: {
    // ──────────────────────────────────────────────────────────────
    // Backstage core surfaces
    // ──────────────────────────────────────────────────────────────
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'unset',
          backgroundColor: boschBlue[50],
          color: boschWhite,
          boxShadow: 'none',
          borderBottom: `1px solid ${boschBlue[40]}`,

          // Hide the upstream `<Header>` from `@backstage/core-components`
          // when it's rendered inside a page wrapped by `BoschPageLayout`
          // that opts into header suppression (currently /rbac — see the
          // `suppressUpstreamHeader` block in
          // packages/app/src/modules/theme/pageLayout.tsx).
          //
          // We can't pass `useHeader: false` to the `@backstage-community/
          // plugin-rbac` Router because its export surface only exposes
          // the alpha PageBlueprint and the legacy routable extension —
          // not the underlying `<Router />` component itself. So we have
          // to dispose of the duplicate header at the CSS layer.
          //
          // The selector targets ancestor `.bosch-suppress-upstream-header`
          // (set by `BoschPageLayout`'s wrapper div) AND the JSS rule
          // generates a class like `BackstageHeader-header-123`. Our
          // own blue banner in `BoschPageLayout` is an inline-styled
          // `<header>` with NO `BackstageHeader-header-XXX` class, so
          // it is NOT matched and remains visible.
          '.bosch-suppress-upstream-header &': {
            display: 'none',
          },
        },
        title: {
          color: boschWhite,
          fontWeight: 600,
        },
        subtitle: {
          color: boschBlue[95],
        },
        type: {
          color: boschBlue[90],
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          color: boschBlue[95],
        },
        value: {
          color: boschWhite,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: boschWhite,
          borderBottom: `1px solid ${boschGray[90]}`,
          paddingLeft: 24,
        },
        defaultTab: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.85rem',
          color: boschGray[45],
          padding: '14px 16px',
          minWidth: 'auto',
        },
        selected: {
          color: boschBlue[50],
        },
        tabRoot: {
          '&:hover': {
            color: boschBlue[40],
            backgroundColor: boschBlue[95],
          },
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: `2px solid ${boschBlue[50]}`,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        selected: {
          borderLeft: `4px solid ${boschBlue[50]}`,
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
          },
        },
        root: {
          transition:
            'background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease',
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: boschGray[95],
        },
      },
    },
    BackstageContentHeader: {
      styleOverrides: {
        title: {
          fontWeight: 600,
          color: boschGray[15],
        },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          borderBottom: `1px solid ${boschGray[90]}`,
          padding: '14px 20px',
        },
        headerTitle: {
          fontSize: '0.95rem',
          fontWeight: 600,
          color: boschGray[15],
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        },
        headerSubheader: {
          color: boschGray[45],
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          color: boschWhite,
          padding: '20px 16px 16px',
          '& .MuiCardHeader-title': {
            color: boschWhite,
            fontWeight: 600,
          },
          '& .MuiCardHeader-subheader': {
            color: boschBlue[95],
          },
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: boschWhite,
          borderBottom: `1px solid ${boschGray[90]}`,
        },
        title: {
          '& > h2': {
            fontWeight: 600,
            fontSize: '1rem',
            color: boschGray[15],
          },
        },
      },
    },
    BackstageEmptyState: {
      styleOverrides: {
        root: {
          padding: 32,
          backgroundColor: boschGray[95],
          borderRadius: 4,
          border: `1px dashed ${boschGray[85]}`,
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Buttons
    // ──────────────────────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0.2,
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: boschBlue[40],
            boxShadow: 'none',
          },
        },
        // Promote outlined-primary to look like contained-primary (filled
        // blue background + white text). Some upstream plugins render the
        // primary CTA as `<Button variant="outlined" color="primary">`
        // (e.g. `RegisterExistingButton` in the scaffolder, see
        // node_modules/@backstage/plugin-scaffolder/dist/alpha/components/
        // TemplateListPage/RegisterExistingButton.esm.js) instead of
        // `variant="contained"`. Without this override that button reads
        // as a secondary ghost button, but in OUR design language the
        // primary action of a page is always solid blue. Catalog/Apis use
        // `<CreateButton>` which is `variant="contained"` and matches
        // the override on `containedPrimary` above; this rule keeps the
        // scaffolder's Register button visually consistent with those
        // siblings.
        //
        // Trade-off: every `<Button variant="outlined" color="primary">`
        // in third-party plugins becomes filled blue. We accept that
        // because:
        //   1. The Bosch design system uses solid primary buttons by
        //      default — outlined is the exception, not the rule.
        //   2. We don't ship any of our own outlined-primary buttons.
        //   3. If a future plugin ships a secondary outlined-primary
        //      button next to a contained-primary CTA, both will look
        //      identical, which is a smaller visual issue than the
        //      current "primary action looks ghosted" problem.
        outlinedPrimary: {
          backgroundColor: boschBlue[50],
          borderColor: boschBlue[50],
          color: boschWhite,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: boschBlue[40],
            borderColor: boschBlue[40],
            boxShadow: 'none',
            color: boschWhite,
          },
        },
        textPrimary: {
          color: boschBlue[50],
          '&:hover': {
            backgroundColor: boschBlue[95],
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: boschGray[45],
          '&:hover': {
            backgroundColor: boschBlue[95],
            color: boschBlue[50],
          },
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Surfaces
    // ──────────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: boschWhite,
        },
        rounded: {
          borderRadius: 4,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: `1px solid ${boschGray[90]}`,
          boxShadow: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '0.95rem',
          fontWeight: 600,
          color: boschGray[15],
        },
        subheader: {
          color: boschGray[45],
          fontSize: '0.8rem',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: boschBlue[50],
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: boschGray[90],
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Tabs
    // ──────────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: boschBlue[50],
          height: 3,
        },
        root: {
          borderBottom: `1px solid ${boschGray[90]}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 48,
          color: boschGray[45],
          '&:hover': {
            color: boschBlue[50],
          },
        },
        textColorInherit: {
          opacity: 1,
          '&$selected': {
            color: boschBlue[50],
          },
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Tables (Backstage Table is built on Material Table + MUI)
    // ──────────────────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontSize: '0.7rem',
          color: boschGray[40],
          backgroundColor: boschGray[95],
          borderBottom: `2px solid ${boschGray[85]}`,
        },
        root: {
          borderBottom: `1px solid ${boschGray[90]}`,
          fontSize: '0.85rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.12s ease',
          '&:hover': {
            backgroundColor: `${boschBlue[95]} !important`,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: `${boschGray[40]} !important`,
          '&:hover': {
            color: `${boschBlue[50]} !important`,
          },
        },
        active: {
          color: `${boschBlue[50]} !important`,
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Lists (sidebars de filtros do Catalog, etc.)
    // ──────────────────────────────────────────────────────────────
    MuiListItem: {
      styleOverrides: {
        root: {
          '&$selected': {
            backgroundColor: boschBlue[95],
            borderLeft: `3px solid ${boschBlue[50]}`,
            '&:hover': {
              backgroundColor: boschBlue[90],
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: boschBlue[95],
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: boschGray[45],
          minWidth: 36,
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Forms (Scaffolder, settings, search filters)
    // ──────────────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: boschWhite,
          '& $notchedOutline': {
            borderColor: boschGray[85],
          },
          '&:hover $notchedOutline': {
            borderColor: boschBlue[55],
          },
          '&$focused $notchedOutline': {
            borderColor: boschBlue[50],
            borderWidth: 2,
          },
        },
        input: {
          padding: '12px 14px',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: boschGray[95],
          '&:hover': {
            backgroundColor: boschGray[90],
          },
          '&$focused': {
            backgroundColor: boschGray[95],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: boschGray[45],
          '&$focused': {
            color: boschBlue[50],
            fontWeight: 600,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.72rem',
          color: boschGray[50],
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorSecondary: {
          color: boschGray[55],
          '&$checked': {
            color: boschBlue[50],
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorSecondary: {
          color: boschGray[55],
          '&$checked': {
            color: boschBlue[50],
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&$checked': {
            color: boschBlue[50],
          },
          '&$checked + $track': {
            backgroundColor: boschBlue[50],
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Feedback (chips, tooltips, links)
    // ──────────────────────────────────────────────────────────────
    MuiLink: {
      styleOverrides: {
        root: {
          color: boschBlue[50],
          fontWeight: 500,
          '&:hover': {
            color: boschBlue[40],
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
          fontSize: '0.72rem',
          height: 24,
        },
        outlined: {
          borderColor: boschGray[85],
          color: boschGray[30],
          backgroundColor: boschWhite,
        },
        colorPrimary: {
          backgroundColor: boschBlue[95],
          color: boschBlue[40],
        },
        colorSecondary: {
          backgroundColor: boschGray[95],
          color: boschGray[15],
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: boschGray[15],
          color: boschWhite,
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 4,
        },
        arrow: {
          color: boschGray[15],
        },
      },
    },

    // ──────────────────────────────────────────────────────────────
    // Loading / progress
    // ──────────────────────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: boschBlue[95],
        },
        barColorPrimary: {
          backgroundColor: boschBlue[50],
        },
      },
    },
  },
});

const boschLightExtension = ThemeBlueprint.make({
  name: 'bosch-light',
  params: {
    theme: {
      id: 'bosch-light',
      title: 'Bosch Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={boschLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    boschLightExtension,
    // Hides the redundant white plugin-icon-and-title strip that the
    // framework's default `PageLayout` renders above every page — see
    // ./pageLayout.tsx for the full rationale.
    boschPageLayoutExtension,
  ],
});
